import { type FC, type FormEvent, useEffect, useRef, useState } from 'react'
import { type MensagemAtendimentoView } from '../../../atendimento/core/_models'
import { type UsuarioWithImage } from '../../../../auth'
import { VideoCallIcon } from './videoCallIcons'

type ChatPanelProps = {
  messages: MensagemAtendimentoView[]
  currentUser?: UsuarioWithImage
  pacienteNome?: string
  doctorNome?: string
  isPaciente?: boolean
  onSendMessage: (text: string) => void
  onClose: () => void
  /** Falha de histórico, conexão ou envio, mostrada acima da conversa. */
  aviso?: string
}

export const ChatPanel: FC<ChatPanelProps> = ({
  messages,
  currentUser,
  pacienteNome,
  doctorNome,
  isPaciente = false,
  onSendMessage,
  onClose,
  aviso,
}) => {
  const [inputText, setInputText] = useState('')
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const isFirstRender = useRef(true)

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    setTimeout(() => {
      scrollContainerRef.current?.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior,
      })
    }, 100)
  }

  useEffect(() => {
    if (isFirstRender.current) {
      scrollToBottom('auto')
      isFirstRender.current = false
      return
    }

    scrollToBottom('smooth')
  }, [messages])

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true })
  }, [])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    if (!inputText.trim()) return

    onSendMessage(inputText)
    setInputText('')
  }

  return (
    <div className='card card-bordered h-100 d-flex flex-column bg-body shadow-sm'>
      <div className='card-header py-3 px-5 border-bottom border-gray-300'>
        <div className='card-title'>
          <h3 className='fw-bold text-gray-900 fs-5 mb-0'>Chat do Atendimento</h3>
        </div>
        <div className='card-toolbar'>
          <button
            onClick={onClose}
            className='btn btn-sm btn-icon btn-active-color-primary btn-active-light-primary'
            type='button'
            title='Fechar chat'
          >
            <i className={`${VideoCallIcon.Close} fs-4 text-gray-500`} />
          </button>
        </div>
      </div>

      {aviso && (
        <div className='alert alert-warning py-2 px-5 mb-0 rounded-0 fs-7' role='status'>
          {aviso}
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className='card-body d-flex flex-column flex-grow-1 overflow-y-auto p-5'
        style={{ minHeight: 0 }}
      >
        {messages.length === 0 ? (
          <div className='flex-grow-1 d-flex flex-column align-items-center justify-content-center text-gray-400 text-center fs-7'>
            <i className={`${VideoCallIcon.Chats} fs-2 mb-2 opacity-50`} />
            <span>Nenhuma mensagem enviada ainda.</span>
          </div>
        ) : (
          messages.map((msg, index) => {
            const isMe = isPaciente
              ? !msg.usuario || msg.usuario.id === -1
              : msg.usuario?.id === currentUser?.id

            return (
              <div
                key={msg.id ?? index}
                className={`d-flex flex-column mb-4 ${
                  isMe ? 'align-items-end' : 'align-items-start'
                }`}
              >
                <span className={`fs-8 fw-bold text-gray-500 mb-1 ${isMe ? 'me-1' : 'ms-1'}`}>
                  {isMe
                    ? 'Você'
                    : msg.usuario?.nome ||
                      (isPaciente ? doctorNome || 'Médico' : pacienteNome || 'Paciente')}
                </span>
                <div
                  className={`p-3 rounded mw-100 shadow-sm text-break ${
                    isMe ? 'bg-light-primary text-gray-900' : 'bg-light-info text-gray-900'
                  }`}
                >
                  <span className='fs-6 fw-semibold'>{msg.mensagem}</span>
                </div>
                <span className='fs-9 text-gray-400 mt-1 mx-1'>
                  {msg.dataFormatada ||
                    (msg.data
                      ? new Date(msg.data).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '')}
                </span>
              </div>
            )
          })
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className='card-footer py-3 px-5 border-top border-gray-300 d-flex gap-2 align-items-center bg-body'
      >
        <input
          ref={inputRef}
          type='text'
          placeholder='Escreva sua mensagem...'
          value={inputText}
          onChange={(event) => setInputText(event.target.value)}
          className='form-control form-control-solid rounded-pill'
        />
        <button
          type='submit'
          className='btn btn-icon btn-primary rounded-circle flex-shrink-0'
          title='Enviar mensagem'
        >
          <i className={VideoCallIcon.Send} />
        </button>
      </form>
    </div>
  )
}
